import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type HrisInstallService from 'garaje/services/hris-install';
import type StateService from 'garaje/services/state';
import { entitledHrisStepIds, HRIS_PER_LOCATION_STEP_IDS } from 'garaje/utils/hris-lifecycle-billing';
import { Permission } from 'garaje/utils/ui-permissions';

export default class HrisDashboardAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare hrisInstall: HrisInstallService;
  @service declare state: StateService;

  // Company-level read → global overview (can see all locations)
  get canVisitGlobal(): boolean {
    if (!this.featureFlags.isEnabled('integration-hris-dashboard')) return false;
    this.hrisInstall.ensureLoaded();
    if (!this.hrisInstall.isInstalled) return false;
    // Billing: a company reader can see any entitled step; zero entitlement
    // hides the dashboard entirely (menu items + routes) — never an empty
    // routine. The HRIS trigger gate (hr-recruiting-integrations) does NOT count.
    if (entitledHrisStepIds(this.state).size === 0) return false;
    return this.authz.hasPermissionAtCurrentCompany(Permission.HRIS_LIFECYCLE_ROUTINE_READ);
  }

  // Any read permission → can visit the dashboard, but only when billing
  // entitles at least one step THAT SCOPE can render: company readers see all
  // steps; location-scoped readers see only per-location (ACS) rows — org steps
  // need company read — so entitlement to org steps alone must not surface the
  // dashboard for them (it would render empty).
  get canVisit(): boolean {
    const ff = this.featureFlags.isEnabled('integration-hris-dashboard');
    if (!ff) return false;
    this.hrisInstall.ensureLoaded();
    if (!this.hrisInstall.isInstalled) return false;
    const entitled = entitledHrisStepIds(this.state);
    if (entitled.size === 0) return false;
    if (this.authz.hasPermissionAtCurrentCompany(Permission.HRIS_LIFECYCLE_ROUTINE_READ)) return true;
    const hasLocationVisibleStep = [...entitled].some((id) => HRIS_PER_LOCATION_STEP_IDS.has(id));
    return hasLocationVisibleStep && this.authz.hasPermissionAtCurrentLocation(Permission.HRIS_LIFECYCLE_ROUTINE_READ);
  }

  // Location-level manage → can write at current location
  get canManage(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.HRIS_LIFECYCLE_ROUTINE_MANAGE);
  }

  // Company-level manage → can write globally
  get canManageAll(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.HRIS_LIFECYCLE_ROUTINE_MANAGE);
  }
}
